import { FC, ReactNode, useId } from 'react';
import { FormControl, FormHelperText, FormLabel, SxProps, Theme } from '@mui/material';

export interface BaseLabelFieldControlProps {
    /** 對應子元件 input 的 id;不傳則自動產生(從 context 取得) */
    htmlFor?: string;
    /** 欄位標題 */
    label: string;
    /** 必填,會在 label 後顯示 * */
    required?: boolean;
    /** 錯誤狀態,子元件(OutlinedInput/Select 等)會透過 FormControl context 自動變紅 */
    error?: boolean;
    /**
     * 說明文字。
     * error=true 時以紅色顯示;error=false 時以一般色顯示(可當欄位提示用)。
     * 不想在無錯誤時顯示提示,就只在有錯誤時傳入。
     */
    helperText?: string;
    /** 停用整組欄位 */
    disabled?: boolean;
    /** 佈局用,直接透傳給 FormControl */
    sx?: SxProps<Theme>;
    fullWidth?: boolean;
    children: ReactNode;
}

const FieldLabel = ({ htmlFor, required, children }: { htmlFor: string; required?: boolean; children: ReactNode }) => {
    return (
        <FormLabel htmlFor={htmlFor} required={required} sx={{ mb: 0.5, fontSize: 13, color: 'var(--text-color)' }}>
            {children}
        </FormLabel>
    );
};

const BaseLabelFieldControl: FC<BaseLabelFieldControlProps> = ({
    htmlFor,
    label,
    required = false,
    error = false,
    helperText,
    disabled = false,
    sx,
    fullWidth = true,
    children,
}: BaseLabelFieldControlProps) => {
    const autoId = useId();
    const inputId = htmlFor ?? autoId;
    const helperId = helperText ? `${inputId}-helper` : undefined;

    return (
        <FormControl fullWidth={fullWidth} required={required} error={error} disabled={disabled} sx={sx}>
            <FieldLabel htmlFor={inputId} required={required}>
                {label}
            </FieldLabel>
            {children}
            {helperText ? (
                <FormHelperText id={helperId} error={error}>
                    {helperText}
                </FormHelperText>
            ) : null}
        </FormControl>
    );
};

export default BaseLabelFieldControl;
